<script lang="ts">
  type Principle = {
    title: string;
    description: string;
  };

  const principles: Principle[] = [
    {
      title: 'Your Data, Your Control',
      description: 'Everything stays on your device. No cloud servers, no data mining, no third-party access.',
    },
    {
      title: 'Free Forever',
      description: 'Core features will always be free. No forced upgrades, no subscription traps.',
    },
    {
      title: 'Bring Your Own AI',
      description: 'Connect Anthropic (Claude) or OpenAI (GPT) using your own API key. You control the model, the cost, and when it\'s used. True AI without lock-in.',
    },
  ];
</script>

<section class="section philosophy-section">
  <div class="container">
    <div class="philosophy-content">
      <div class="text-content">
        <h2>Built for Privacy and Simplicity</h2>
        <p class="lead">
          Your financial data belongs to you, and you shouldn't need a subscription to access it. Built by an independent developer for privacy-conscious families.
        </p>
        <p class="description">
          Keep is designed for thoughtful financial reviews on your schedule (monthly, quarterly, or yearly), not daily tracking. Keep intentionally avoids third-party bank connectors. CSV imports take seconds and eliminate account linking, re-authentication issues, and external data access. For periodic reviews, this gives you complete control with zero maintenance.
        </p>
        <div class="principles">
          {#each principles as principle}
            <div class="principle-item">
              <div class="principle-title">{principle.title}</div>
              <div class="principle-description">{principle.description}</div>
            </div>
          {/each}
        </div>
      </div>
      <div class="stats-sidebar">
        <div class="stat-card">
          <div class="stat-value">$0</div>
          <div class="stat-label">Annual Cost</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">100%</div>
          <div class="stat-label">Local Storage</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">0</div>
          <div class="stat-label">Data Collection</div>
        </div>
      </div>
    </div>
  </div>
</section>

<style>
  .philosophy-section {
    /* Background and padding controlled by parent Landing.svelte */
  }

  .philosophy-content {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: var(--spacing-2xl);
    align-items: start;
  }

  .text-content h2 {
    font-size: var(--font-size-3xl);
    margin-bottom: var(--spacing-md);
  }

  .lead {
    font-size: var(--font-size-lg);
    color: var(--color-text-secondary);
    margin-bottom: var(--spacing-md);
    font-weight: var(--font-weight-medium);
  }

  .description {
    color: var(--color-text-secondary);
    line-height: var(--line-height-normal);
    margin-bottom: var(--spacing-xl);
    font-size: var(--font-size-sm);
  }

  .principles {
    display: grid;
    gap: var(--spacing-md);
  }

  .principle-item {
    padding: var(--spacing-md);
    background: var(--color-background);
    border-left: 3px solid var(--color-primary);
    border-radius: var(--radius-md);
  }

  .principle-item:nth-child(1) {
    border-left-color: var(--color-primary);
    background: linear-gradient(to right, #d1fae5 0%, var(--color-background) 100%);
  }

  .principle-item:nth-child(2) {
    border-left-color: #f59e0b;
    background: linear-gradient(to right, #fef3c7 0%, var(--color-background) 100%);
  }

  .principle-item:nth-child(3) {
    border-left-color: #8b5cf6;
    background: linear-gradient(to right, #ede9fe 0%, var(--color-background) 100%);
  }

  .principle-title {
    font-weight: var(--font-weight-semibold);
    font-size: var(--font-size-base);
    color: var(--color-text-primary);
    margin-bottom: var(--spacing-xs);
  }

  .principle-description {
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    line-height: var(--line-height-normal);
  }

  .stats-sidebar {
    display: grid;
    gap: var(--spacing-md);
  }

  .stat-card {
    background: var(--color-background);
    border: 2px solid var(--color-border);
    border-radius: var(--radius-lg);
    padding: var(--spacing-lg);
    text-align: center;
    transition: all var(--transition-base);
  }

  .stat-card:nth-child(1) {
    border-color: #10b981;
  }

  .stat-card:nth-child(1):hover {
    border-color: #10b981;
    background: linear-gradient(135deg, #d1fae5 0%, var(--color-background) 100%);
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }

  .stat-card:nth-child(2) {
    border-color: #3b82f6;
  }

  .stat-card:nth-child(2):hover {
    border-color: #3b82f6;
    background: linear-gradient(135deg, #dbeafe 0%, var(--color-background) 100%);
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }

  .stat-card:nth-child(3) {
    border-color: #8b5cf6;
  }

  .stat-card:nth-child(3):hover {
    border-color: #8b5cf6;
    background: linear-gradient(135deg, #ede9fe 0%, var(--color-background) 100%);
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }

  .stat-card:nth-child(1) .stat-value {
    color: #10b981;
  }

  .stat-card:nth-child(2) .stat-value {
    color: #3b82f6;
  }

  .stat-card:nth-child(3) .stat-value {
    color: #8b5cf6;
  }

  .stat-value {
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-bold);
    margin-bottom: var(--spacing-xs);
  }

  .stat-label {
    font-size: var(--font-size-xs);
    color: var(--color-text-secondary);
    font-weight: var(--font-weight-medium);
  }

  @media (max-width: 968px) {
    .philosophy-content {
      grid-template-columns: 1fr;
    }

    .text-content h2 {
      font-size: var(--font-size-2xl);
    }

    .stats-sidebar {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  @media (max-width: 640px) {
    .stats-sidebar {
      grid-template-columns: 1fr;
    }
  }
</style>
